// src/components/issues/ChoixTraitementQualite.tsx — SCE Issues
// Badges de traitement qualité en bulles + un champ de référence par badge
// FNC-T / NC-S coché.
//
// Une seule saisie pour deux endroits (30/09) : la carte « Traitement
// qualité » (préparé avant la clôture) et la clôture, quand rien n'a été
// préparé. Mêmes bulles, mêmes règles — l'utilisateur ne doit pas apprendre
// deux écrans pour la même chose.

import type { JSX } from 'react'
import { Bulle, GroupeBulles } from '@/components/ui/Bulle'
import { ChampReference, type SaisieReference } from '@/components/issues/ChampReference'
import {
  BADGES_AVEC_REFERENCE,
  BADGES_CLOTURE,
  basculerBadge,
  composerReference,
  decomposerReference,
  type BadgeAvecReference,
  type BadgeCloture,
  type Issue,
} from '@/types/issue.types'
import type { ReferencesQualite } from '@/api/issues.api'

export type SaisiesReferences = Record<BadgeAvecReference, SaisieReference>

const ANNEE_COURANTE = String(new Date().getFullYear())

/** Saisies pré-remplies depuis le problème ; année en cours à défaut. */
export function saisiesDepuis(issue?: Pick<Issue, 'refFnct' | 'refNcs'>): SaisiesReferences {
  const vide = { annee: ANNEE_COURANTE, numero: '' }
  return {
    'FNC-T': decomposerReference(issue?.refFnct) ?? vide,
    'NC-S': decomposerReference(issue?.refNcs) ?? vide,
  }
}

/**
 * Références composées depuis la saisie. Un numéro vide = pas de référence
 * (facultative) ; une année tronquée = saisie à compléter (`incomplete`), qui
 * bloque l'enregistrement plutôt que d'écrire une référence introuvable.
 */
export function composerReferences(
  badges: readonly BadgeCloture[],
  saisies: SaisiesReferences,
): { refs: ReferencesQualite; incomplete: BadgeAvecReference | null } {
  const refs: ReferencesQualite = {}
  let incomplete: BadgeAvecReference | null = null
  for (const badge of BADGES_AVEC_REFERENCE) {
    if (!badges.includes(badge)) continue
    const saisie = saisies[badge]
    if (saisie.numero.trim() === '') continue
    const ref = composerReference(badge, saisie.annee, saisie.numero)
    if (ref === null) incomplete ??= badge
    else if (badge === 'FNC-T') refs.fnct = ref
    else refs.ncs = ref
  }
  return { refs, incomplete }
}

const STYLE_ETIQUETTE = {
  fontSize: 'var(--font-size-info)',
  color: 'var(--color-text-secondary)',
  display: 'block',
  marginBottom: 6,
} as const

export function ChoixTraitementQualite({
  idPrefixe,
  badges,
  onBadges,
  saisies,
  onSaisies,
  incomplete,
  desactive,
}: {
  /** Préfixe des id de champs : deux saisies peuvent coexister sur la fiche. */
  idPrefixe: string
  badges: BadgeCloture[]
  onBadges: (badges: BadgeCloture[]) => void
  saisies: SaisiesReferences
  onSaisies: (saisies: SaisiesReferences) => void
  incomplete: BadgeAvecReference | null
  desactive: boolean
}): JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      {/* La règle d'exclusivité d'« Autre » vit dans basculerBadge(), pas
          ici : l'interface l'applique, l'API la vérifie à nouveau. */}
      <GroupeBulles libelle="Traitement qualité" multiple>
        {BADGES_CLOTURE.map((b) => (
          <Bulle
            key={b}
            role="checkbox"
            libelle={b}
            selectionne={badges.includes(b)}
            onClick={() => onBadges(basculerBadge(badges, b))}
            desactive={desactive}
          />
        ))}
      </GroupeBulles>

      {/* Un champ par badge à référence coché, et seulement lui : une
          référence sans son badge serait une donnée orpheline. */}
      {BADGES_AVEC_REFERENCE.filter((b) => badges.includes(b)).map((b) => (
        <div key={b}>
          <label htmlFor={`${idPrefixe}-${b}`} style={STYLE_ETIQUETTE}>
            N° de {b} <span style={{ color: 'var(--color-gray-70)' }}>— si déjà connu</span>
          </label>
          <ChampReference
            id={`${idPrefixe}-${b}`}
            badge={b}
            valeur={saisies[b]}
            onChange={(v) => onSaisies({ ...saisies, [b]: v })}
            desactive={desactive}
            invalide={incomplete === b}
          />
        </div>
      ))}
    </div>
  )
}
